import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [count, setCount] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    fetch(`${API}/waitlist/count`)
      .then((r) => r.json())
      .then((d) => setCount(d.count))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) throw new Error();
      setJoined(true);
      setCount((c) => (c ? c + 1 : c));
      toast.success("You're on the list! We'll be in touch soon.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email address.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="waitlist" className="relative py-32 md:py-44 border-t border-white/10 overflow-hidden">
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-0 w-[45rem] h-[45rem] bg-[#0055FF]/15 blur-[140px] rounded-full" />
      <div className="max-w-4xl mx-auto px-6 text-center relative">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-display font-extrabold text-5xl md:text-7xl tracking-tighter leading-[0.95] text-glow"
        >
          {t("footer.ctaTitle1")}<br />{t("footer.ctaTitle2")}
        </motion.h2>
        <p className="text-slate-400 text-lg mt-6 max-w-xl mx-auto">
          {t("footer.ctaSubtitle")}
        </p>

        {joined ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-10 inline-flex items-center gap-2 bg-[#0055FF]/15 border border-[#0055FF]/40 text-[#00F0FF] px-6 py-4 rounded-2xl font-mono text-sm"
            data-testid="waitlist-success"
          >
            {t("footer.success")}
          </motion.div>
        ) : (
          <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="waitlist-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("footer.emailPlaceholder")}
              className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-slate-600 outline-none focus:border-[#0055FF] transition-colors duration-300"
              data-testid="waitlist-email"
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 bg-white text-black px-7 py-4 rounded-2xl font-semibold hover:bg-[#00F0FF] disabled:opacity-50 transition-colors duration-300"
              data-testid="waitlist-submit"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>{t("footer.join")} <ArrowRight size={16} /></>}
            </button>
          </form>
        )}

        {count != null && (
          <p className="mt-6 font-mono text-xs text-slate-500" data-testid="waitlist-count">
            {formatNumber(count)}+ {t("footer.buildersWaiting")}
          </p>
        )}

        <div className="mt-24 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-[#0055FF] flex items-center justify-center font-display font-extrabold text-sm">A</div>
            <span className="font-display font-bold">ArcaCentum<span className="text-[#00F0FF]">.ai</span></span>
          </div>
          <p className="font-mono text-xs text-slate-600">© 2026 ArcaCentum.ai · {t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
}
